import { useState, useEffect, useCallback } from "react";
import { Redirect, useHistory, Link } from "react-router-dom";
import { CircularProgress } from "@material-ui/core";
import { z } from "zod";
import {
  Container,
  PromptText,
  FormContainer,
  TextContainer,
  StyledHeadings,
  ButtonSeparator,
  ProgressContainer,
} from "pages/styles/login";
import { Notification } from "storybook/components/notification";
import { Typography } from "storybook/components/typography";
import { Button } from "storybook/components/button";
import { useForm } from "storybook/utils/form";
import { PrivacyLink } from "components/privacy-link";
import { preserveQueryParams } from "helpers";
import { emailPasswordErrorMsg, usePatientAuth } from "context/patient-auth";
import {
  Row,
  ButtonsWrapper,
  BannerContainer,
  ForgotPasswordContainer,
} from "./styles";

const patientSchema = z.object({
  patient: z
    .object({
      email: z.string().email({ message: "Please enter a valid email " }),
      password: z.string().nonempty({ message: "Please enter a password" }),
    })
    .required()
    .strict(),
});

type Patient = z.infer<typeof patientSchema>;

export default function EmailPassword() {
  const history = useHistory();
  const {
    isSubmitting,
    setSubmitting,
    isAuthenticated,
    submitCredentials,
    emailPasswordError,
    setPasswordlessError,
    setEmailPasswordError,
  } = usePatientAuth();
  const [formState, setFormState] = useState<Patient>({
    patient: {
      email: "",
      password: "",
    },
  });

  const { Form, Input, watch, getFieldState } = useForm<Patient>({
    schema: patientSchema,
    values: formState,
    shouldFocusError: true,
  });

  const fieldError = Boolean(
    getFieldState("patient.email").error ||
      getFieldState("patient.password").error
  );

  const emailValue = watch("patient.email");

  const handleSubmit = useCallback(
    (data: Patient) => {
      setSubmitting(true);
      setEmailPasswordError(undefined);

      setFormState(data);

      submitCredentials(data.patient.email, data.patient.password);
    },
    [setEmailPasswordError, setSubmitting, submitCredentials]
  );

  const handlePasswordlessClick = useCallback(() => {
    setPasswordlessError(false);

    if (emailValue) {
      sessionStorage.setItem("prepopulateEmail", emailValue);
    }

    history.push({
      pathname: "/login/code",
      search: location.search,
    });
  }, [emailValue, history, setPasswordlessError]);

  useEffect(() => {
    if (fieldError) {
      setSubmitting(false);
      setEmailPasswordError(undefined);
    }
  }, [fieldError, setEmailPasswordError, setSubmitting]);

  if (isAuthenticated) {
    return <Redirect to={preserveQueryParams("/")} />;
  }

  const isDisabled = isSubmitting && !emailPasswordError;

  return (
    <Container>
      <StyledHeadings data-id="StyledHeadings">
        <Typography
          theme="default"
          variant="h3"
          color="ebonyClay"
          component="h1"
        >
          Get your personalized cancer risk results
        </Typography>
      </StyledHeadings>
      <TextContainer>
        <Typography
          theme="default"
          variant="subtitle1"
          color="ebonyClay"
          component="p"
        >
          <strong>First time here?</strong> Enter your email to securely begin
          your cancer risk assessment. This helps us personalize your results.
        </Typography>
        <br />
        <Typography
          theme="default"
          variant="subtitle1"
          color="ebonyClay"
          component="p"
        >
          <strong>Been here before?</strong> Enter your email (or unique email
          ID and password) to continue your assessment or start a new one.
        </Typography>
        <PromptText>
          <Typography
            theme="default"
            variant="subtitle1"
            color="ebonyClay"
            component="p"
          >
            <strong>Sign in manually with email and password</strong>
          </Typography>
        </PromptText>
      </TextContainer>
      {!fieldError && emailPasswordError && (
        <BannerContainer>
          <Notification
            theme="default"
            variant="warning"
            fullWidth={true}
            title={emailPasswordErrorMsg[emailPasswordError]}
          />
        </BannerContainer>
      )}
      <FormContainer>
        <Form onSubmit={handleSubmit}>
          <Row>
            <Input
              hideDirty
              theme="default"
              type="email"
              label="Email address"
              name="patient.email"
              labelPosition="top"
              alignText="left"
            />
          </Row>
          <Row>
            <Input
              hideDirty
              theme="default"
              type="password"
              label="Password"
              name="patient.password"
              labelPosition="top"
              alignText="left"
            />
          </Row>
          <ForgotPasswordContainer>
            <Link
              className="forgot-link"
              to={preserveQueryParams("/login/forgot")}
            >
              Forgot password?
            </Link>
          </ForgotPasswordContainer>
          <ButtonsWrapper>
            <Button
              type="submit"
              theme="default"
              variant="contained"
              color="secondary"
              disabled={isDisabled}
            >
              Continue
            </Button>
            <ButtonSeparator>
              <Typography
                theme="default"
                variant="subtitle2"
                color="ebonyClay"
                component="span"
              >
                Or
              </Typography>
            </ButtonSeparator>
            <Button
              type="button"
              theme="default"
              variant="outlined"
              disabled={isSubmitting}
              onClick={handlePasswordlessClick}
            >
              Send Me a Code
            </Button>
            {isSubmitting && (
              <ProgressContainer>
                <CircularProgress size={30} />
              </ProgressContainer>
            )}
          </ButtonsWrapper>
          <PrivacyLink />
        </Form>
      </FormContainer>
    </Container>
  );
}
